import { useSyncExternalStore } from 'react';

export const DEMO_STATUS = 'DEMO';

export type OnboardingDialogChromeGateInput = {
  readonly dialogOpen: boolean;
  readonly externalOnly: boolean;
  readonly isEmbedded: boolean;
  readonly hasOrgOptions: boolean;
  readonly genoWizardActive: boolean;
  readonly hierarchyChartWizardActive: boolean;
  readonly status: string | null;
  readonly dismissed: boolean;
  readonly anonymousAccess: boolean;
  readonly delayOrgDemoSignup: boolean;
};

/**
 * Whether editor chrome (startup shape picker) must wait for the onboarding /
 * demo-signup iframe. True when the dialog is already open, or when the demo
 * auto-prompt will open on this session (before setOpen has flushed).
 */
export function shouldBlockStartupChromeForOnboardingDialog(
  input: OnboardingDialogChromeGateInput,
): boolean {
  if (input.dialogOpen) {
    return true;
  }

  if (input.externalOnly || input.isEmbedded) {
    return false;
  }

  if (input.hasOrgOptions || input.genoWizardActive || input.hierarchyChartWizardActive) {
    return false;
  }

  if (input.status !== DEMO_STATUS || input.dismissed || input.anonymousAccess) {
    return false;
  }

  // Delayed org demo signup opens later; do not hold the blank-canvas picker.
  if (input.delayOrgDemoSignup) {
    return false;
  }

  return true;
}

let blocksChrome = false;
const listeners = new Set<() => void>();

export function getOnboardingDialogBlocksChrome(): boolean {
  return blocksChrome;
}

export function subscribeOnboardingDialogBlocksChrome(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

export function setOnboardingDialogBlocksChrome(next: boolean): void {
  if (blocksChrome === next) {
    return;
  }
  blocksChrome = next;
  for (const listener of listeners) {
    listener();
  }
}

export function useOnboardingDialogBlocksChrome(): boolean {
  return useSyncExternalStore(
    subscribeOnboardingDialogBlocksChrome,
    getOnboardingDialogBlocksChrome,
    getOnboardingDialogBlocksChrome,
  );
}
